/**
 * Event location-label helpers.
 *
 * Several surfaces (card, detail-page address block, tour-dates row, OG
 * description) render a `"City, State"` string. Many US entries have
 * `city === state` (e.g. "New York, New York" / "Washington, Washington")
 * and the duplication reads sloppy — these helpers collapse the state when
 * it matches the city (case-insensitive).
 */

function _eqCI(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/**
 * Return `state` iff it's not just a repeat of `city`. Lets callers write
 *
 *   [event.city, stateIfDistinct(event.city, event.state)]
 *     .filter(Boolean).join(', ')
 *
 * …without special-casing in every usage.
 */
export function stateIfDistinct(
  city: string | null | undefined,
  state: string | null | undefined,
): string | null {
  if (!state) return null;
  if (_eqCI(city, state)) return null;
  return state;
}

/**
 * Compose "City, State" with the dedupe applied. Returns an empty string
 * when neither is present so callers can treat the result as truthy/falsy.
 */
export function cityStateLabel(
  city: string | null | undefined,
  state: string | null | undefined,
): string {
  const parts = [city || null, stateIfDistinct(city, state)].filter(Boolean) as string[];
  return parts.join(', ');
}
